import WidgetCard from '../../../../../components/WidgetCard'
import { PolicyParametersIcon } from '../../components/common/icon'
import { EmptyStateWithAction } from '../../../../../components/EmptyStateWithAction'
import { usePolicyParametersContext } from '../../context/policyParametersContext'
import {
  ACTION_POLICY_PARAMETERS_DELETE,
  ACTION_POLICY_PARAMETERS_FETCH,
  deletePolicyParameter,
  fetchPolicyParameters
} from '../../policySettingsSlice'
import { useReduxSelector } from '../../../../../hooks/useReduxSelector'
import { getActionState, getShowLoader, resetAction } from '../../../../../reducers/requestReducer'
// import { PolicyParameterListItem } from '../../types'
import { getUpdatedUrl } from '../../../../../utils/urlUtil'
import { BASE_PATH } from '../../constants'
import { EMPTY_CONTENT, URL_POLICY_PARAMETERS } from '../../../../../constants'
import TitleColumnWithTooltip from '../../../../../components/dataTable/components/titleColumn'
import RegularColumn from '../../../../../components/dataTable/components/regularColumn'
import TableCardViewV2, {
  ListItemData
} from '../../../../../components/TableCardViewV2/tableCardViewV2'
import { STATES } from '../../../../../interfaces'
import { addNotification } from '../../../../notification/notificationSlice'
import PopupConfirm from '../../../../../components/PopupConfirm/popupConfirm'
import EnableDisableParam from '../../components/enableDisableParam'
import { PolicyParameter } from '../../types'
import { ProtectedContent } from '../../../../../HOCs/ProtectedContent'
import { ACTIONS } from '../../../../../components/PermissionMapper/constants'
import { usePermission } from '../../../../../HOCs/PermissionProvider'
import { useDispatch } from 'react-redux'
import { Plus } from 'phosphor-react'
import { Button, Toggle, Tooltip, Typography } from '@lightbeamai/design-system'
import { useIntl } from 'react-intl'
import React, { useEffect, useState } from 'react'
import { useLocation } from 'react-router-dom'

export const PolicyParameters = () => {
  const intl = useIntl()
  const dispatch = useDispatch()
  const location = useLocation()
  const [paramToDelete, setParamToDelete] = useState<string>()
  const [paramToEnable, setParamToEnable] = useState<PolicyParameter>()
  const { navigateToCreateOrEditParameter } = usePolicyParametersContext()

  const handleCreateClick = () => {
    navigateToCreateOrEditParameter()
  }

  const showLoader = useReduxSelector(() => getShowLoader(ACTION_POLICY_PARAMETERS_FETCH))
  const showDeleteLoader = useReduxSelector(() => getShowLoader(ACTION_POLICY_PARAMETERS_DELETE))
  const deleteActionState = useReduxSelector(() => getActionState(ACTION_POLICY_PARAMETERS_DELETE))
  const policyParamerters = useReduxSelector((state) => state.policySettings.policyParamerters.list)

  const hasActionsAccess = usePermission().hasAction(ACTIONS.POLICY_ACTIONS)

  // Fetch on mount and when returning from create/edit screen
  useEffect(() => {
    dispatch(fetchPolicyParameters())
  }, [dispatch, location.pathname])

  useEffect(() => {
    if (deleteActionState === STATES.success) {
      dispatch(addNotification({ success: 'policy.settings.paramerters.delete.success' }))
      setParamToDelete(undefined)
      dispatch(fetchPolicyParameters())
    } else if (deleteActionState === STATES.failed) {
      dispatch(addNotification({ error: 'policy.settings.paramerters.delete.error' }))
    }
    resetAction(ACTION_POLICY_PARAMETERS_DELETE)
  }, [deleteActionState, dispatch])

  const handleDelete = () => {
    if (paramToDelete) {
      dispatch(deletePolicyParameter(paramToDelete))
    }
  }

  const columns = [
    {
      title: intl.formatMessage({ id: 'policy.settings.paramerters' }),
      dataKey: 'name',
      minWidth: 150,
      cellRenderer: function RegularRenderer({ data }) {
        const { id, name = '' } = data
        const url = getUpdatedUrl({
          pathName: `${BASE_PATH}${URL_POLICY_PARAMETERS}/${id}`
        })
        return <TitleColumnWithTooltip tooltip={name} content={name || EMPTY_CONTENT} link={url} />
      }
    },

    {
      title: intl.formatMessage({
        id: 'attributeManagementSettings.customAttributes.enablementStatus'
      }),
      dataKey: 'enablementStatus',
      minWidth: 150,
      cellRenderer: function RegularRenderer({ data }) {
        const { enabled = '', isUsedInPolicy } = data
        // const handleChange = () => setToggledPolicyParameter(data)
        return (
          <RegularColumn
            tooltip={enabled}
            showTooltip={false}
            content={
              <Tooltip
                arrow
                title={
                  isUsedInPolicy ? (
                    <Typography as="span" variant="baseMinus2" color="white">
                      {intl.formatMessage({ id: 'policy.settings.paramerters.disabledText' })}
                    </Typography>
                  ) : enabled ? (
                    <Typography as="span" variant="baseMinus2" color="white">
                      {intl.formatMessage({ id: 'policy.settings.paramerters.tooltip.enabled' })}
                    </Typography>
                  ) : (
                    <Typography as="span" variant="baseMinus2" color="white">
                      {intl.formatMessage({ id: 'policy.settings.paramerters.tooltip.disabled' })}
                    </Typography>
                  )
                }
                placement="top"
              >
                <Toggle
                  size={16}
                  disabled={isUsedInPolicy}
                  value={enabled}
                  onChange={() => setParamToEnable(data)}
                />
              </Tooltip>
            }
          />
        )
      }
    }
  ]

  const tableData = { list: (policyParamerters as unknown) as ListItemData[], columns }
  const isEmpty = !policyParamerters || policyParamerters?.length === 0

  return (
    <WidgetCard
      widgetTitle={{
        title: intl.formatMessage({ id: 'policy.settings.paramerters' }),
        icon: <PolicyParametersIcon />,
        bordered: true,
        action: (
          <ProtectedContent actions={[ACTIONS.POLICY_ACTIONS]} showContent={!isEmpty}>
            <Button variant="primary" onClick={handleCreateClick} icon={<Plus />}>
              {intl.formatMessage({
                id: 'policy.settings.paramerters.create'
              })}
            </Button>
          </ProtectedContent>
        )
      }}
      className="xs-pb-24"
    >
      {isEmpty && !showLoader ? (
        <EmptyStateWithAction
          info={
            <>
              <Typography>
                {intl.formatMessage({
                  id: 'policy.settings.parameters.empty.text_1'
                })}
              </Typography>
              <Typography className="xs-mt-8">
                {intl.formatMessage({
                  id: 'policy.settings.parameters.empty.text_2'
                })}
              </Typography>
            </>
          }
          title={intl.formatMessage({
            id: 'policy.settings.paramerters.emptyText'
          })}
          actionButtonText={intl.formatMessage({
            id: 'policy.settings.paramerters.create'
          })}
          onClick={hasActionsAccess ? handleCreateClick : undefined}
        />
      ) : (
        <TableCardViewV2
          tableData={tableData}
          pageName={URL_POLICY_PARAMETERS}
          showLoader={showLoader}
          totalCount={policyParamerters?.length || 0}
          legend={intl.formatMessage({ id: 'policy.settings.paramerters' })}
          showControls={false}
        />
      )}
      <PopupConfirm
        dismissible
        isConfirmBtnNegative
        isLoading={showDeleteLoader}
        header={intl.formatMessage({ id: 'policy.settings.paramerters.delete.confirm.header' })}
        content={intl.formatMessage({ id: 'policy.settings.paramerters.delete.confirm' })}
        open={!!paramToDelete}
        onConfirm={() => handleDelete()}
        onCancel={() => setParamToDelete(undefined)}
        confirmButton={intl.formatMessage({ id: 'btn.delete' })}
      />
      <EnableDisableParam
        param={paramToEnable?.id}
        isEnabled={!!paramToEnable?.enabled}
        onClose={() => setParamToEnable(undefined)}
      />
    </WidgetCard>
  )
}
